import React from "react";
import { AITrafficShapingView } from "@tiny/api";
import { useRefreshRegister } from "@tiny/components/src/RefreshProvider";
import useDebounce from "@tiny/components/src/hooks/useDebounce";
import { useRefCallBack } from "@tiny/components/src/hooks/useRefCallBack";
import useSelectable from "@tiny/components/src/hooks/useSelectable";
import { TrafficShapingProps } from "./TrafficShapingList.types";
import { SortOption } from "./TrafficShapingFilter/TrafficShapingFilter.types";
import useLocalStorage from "@tiny/components/src/hooks/useLocalStorage";
import {
  useDeleteTrafficShapingMutation,
  useGetTrafficShapingQuery,
  useResetTrafficShapingMutation,
  useActivateRecurringAITrafficShapingMutation,
} from "@tiny/api";

export const sortOptions: SortOption[] = [
  { label: "Name", value: ["name,ASC"] },
  { label: "Modified Date", value: ["updatedAt,DESC"], action: "Modified" },
  { label: "Created Date", value: ["createdAt,DESC"], action: "Created" },
];

const itemsPerPageOptions = [5, 10, 15, 20];

export function useTrafficShaping(props: TrafficShapingProps) {
  const {
    enableBulkDelete = false,
    enableSelectionActions = true,
    onAfterDelete,
    selectionDisabled,
    selected: selectedProp,
    setSelected: setSelectedProp,
    visible = true,
    // consumed by rows (not a DOM attr) — keep out of boxProps (spread onto <Box>).
    seeAllSubcribersOnUpload,
    ...boxProps
  } = props;

  const { deselectAll, selected, toggleSelect, isSelected } =
    useSelectable<AITrafficShapingView>({
      idKey: "id",
      selected: selectedProp,
      setSelected: setSelectedProp,
    });

  const [storeAITrafficShapingPageSize, setStoreAITrafficShapingPageSize] =
    useLocalStorage("aiTrafficShapingPageSize", itemsPerPageOptions[0]);

  const [searchKeyword, setSearchKeyword] = React.useState("");
  const searchTextName = useDebounce(searchKeyword);
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(storeAITrafficShapingPageSize);
  const [sortOption, setSortOption] = React.useState(sortOptions[2]);

  const [selectedTrafficControlId, setSelectedTrafficControlId] =
    React.useState<string>("");

  const [showTrafficShapingDialog, setShowTrafficShapingDialog] =
    React.useState(false);

  const [showInsufficientDataDialog, setShowInsufficientDataDialog] =
    React.useState(false);

  const [
    showAITrafficControlDetailsDialog,
    setShowAITrafficControlDetailsDialog,
  ] = React.useState(false);

  const openAITrafficControlDetailsDialog = React.useCallback((id: string) => {
    setSelectedTrafficControlId(id);
    setShowAITrafficControlDetailsDialog(true);
  }, []);

  const closeAITrafficControlDetailsDialog = React.useCallback(() => {
    setSelectedTrafficControlId("");
    setShowAITrafficControlDetailsDialog(false);
  }, []);

  const openTrafficShapingDialog = React.useCallback(() => {
    setShowTrafficShapingDialog(true);
  }, []);

  const closeTrafficShapingDialog = React.useCallback(() => {
    setShowTrafficShapingDialog(false);
  }, []);

  const closeInsufficientDataDialog = React.useCallback(() => {
    setShowInsufficientDataDialog(false);
  }, []);

  const onPageChange = React.useCallback((page: number) => {
    setPage(page);
  }, []);

  const onItemsPerPageChange = React.useCallback(
    (size: number) => {
      setSize(size);
      setStoreAITrafficShapingPageSize(size);
    },
    [setStoreAITrafficShapingPageSize]
  );

  const isItemSelected = React.useCallback(
    (item: AITrafficShapingView) => {
      return isSelected(item);
    },
    [isSelected]
  );

  const selectItem = React.useCallback(
    (item: AITrafficShapingView) => {
      toggleSelect(item, true);
    },
    [toggleSelect]
  );

  const unselectItem = React.useCallback(
    (item: AITrafficShapingView) => {
      toggleSelect(item, false);
    },
    [toggleSelect]
  );

  const unselectAll = React.useCallback(() => {
    deselectAll();
  }, [deselectAll]);

  const nrOfSelectedAITrafficShapingItemss = React.useMemo(
    () => selected.length,
    [selected]
  );

  const toggleSelectItem = React.useCallback(
    (item: AITrafficShapingView) => {
      isItemSelected(item) ? unselectItem(item) : selectItem(item);
    },
    [isItemSelected, selectItem, unselectItem]
  );

  React.useLayoutEffect(() => {
    setPage(0);
  }, [searchTextName, size, sortOption]);

  const {
    data: ai_traffic_items,
    refetch: refetchAITrafficShapping,
    isFetching: isAITrafficShappingLoading,
  } = useGetTrafficShapingQuery(
    { page, size, sort: sortOption.value, searchTextName },
    {
      keepPreviousData: true,
    }
  );

  useRefreshRegister(async () => {
    await refetchAITrafficShapping();
  });

  const reloadAITrafficShapingItems = useRefCallBack(
    React.useCallback(() => {
      refetchAITrafficShapping();
    }, [refetchAITrafficShapping])
  );

  const deleteAITrafficShapingMutation = useDeleteTrafficShapingMutation();

  const onDeleteAITrafficShaping = React.useCallback(
    async (ts: AITrafficShapingView) => {
      ts.id &&
        (await deleteAITrafficShapingMutation.mutateAsync(
          { id: ts.id },
          {
            onSuccess: () => {
              unselectItem(ts);
              reloadAITrafficShapingItems();
              onAfterDelete?.();
            },
          }
        ));
    },
    [
      deleteAITrafficShapingMutation,
      onAfterDelete,
      reloadAITrafficShapingItems,
      unselectItem,
    ]
  );

  const resetAITrafficShapingMutation = useResetTrafficShapingMutation();
  const activateRecurringAITrafficShapingMutation =
    useActivateRecurringAITrafficShapingMutation();

  const onResetAITrafficShaping = React.useCallback(
    async (ts: AITrafficShapingView) => {
      if (!ts.id) return;

      await resetAITrafficShapingMutation.mutateAsync(
        { id: ts.id },
        {
          onSuccess: () => {
            reloadAITrafficShapingItems();
          },
          onError: (err) => {
            console.error("Reset failed:", err);
          },
        }
      );
    },
    [resetAITrafficShapingMutation, reloadAITrafficShapingItems]
  );

  const onDeleteAITrafficShapings = React.useCallback(() => {
    const ids = selected.map((item) => item.id!);
    ids.length > 0 &&
      deleteAITrafficShapingMutation.mutateAsync(
        { id: ids[0] },
        {
          onSuccess: () => {
            unselectAll();
            reloadAITrafficShapingItems();
            onAfterDelete?.();
          },
        }
      );
  }, [
    deleteAITrafficShapingMutation,
    onAfterDelete,
    reloadAITrafficShapingItems,
    selected,
    unselectAll,
  ]);

  const isAITrafficShapingDeletable = React.useCallback(
    (ts: AITrafficShapingView) => !ts?.assignedToCampaigns,
    []
  );

  const deleteDisabled = React.useCallback(() => {
    return false;
  }, [selected]);

  const isEmpty =
    !isAITrafficShappingLoading && ai_traffic_items?.content?.length === 0;

  const ai_traffic_items_Ids = React.useMemo(() => {
    return selected?.map((audience) => audience?.id || "") || [];
  }, [selected]);

  const onRecurringToggle = React.useCallback(
    async (ts: AITrafficShapingView) => {
      if (!ts.id) return;

      await activateRecurringAITrafficShapingMutation.mutateAsync(
        { id: ts.id, activateRecurring: !ts.recurring },
        {
          onSuccess: () => {
            reloadAITrafficShapingItems();
          },
          onError: (err) => {
            console.error("Reset failed:", err);
          },
        }
      );
    },
    [activateRecurringAITrafficShapingMutation, reloadAITrafficShapingItems]
  );

  return {
    isAITrafficShappingLoading,
    ai_traffic_items,
    boxProps,
    deleteDisabled,
    enableBulkDelete,
    enableSelectionActions,
    isAITrafficShapingDeletable,
    isEmpty,
    isItemSelected,
    itemsPerPage: size,
    itemsPerPageOptions,
    nrOfSelectedAITrafficShapingItemss,
    onDeleteAITrafficShaping,
    onDeleteAITrafficShapings,
    onItemsPerPageChange,
    onPageChange,
    page,
    reloadAITrafficShapingItems,
    searchKeyword,
    selected,
    selectItem,
    selectionDisabled,
    setSearchKeyword,
    setSortOption,
    sortOption,
    sortOptions,
    toggleSelectItem,
    unselectAll,
    unselectItem,
    visible,
    ai_traffic_items_Ids,
    onResetAITrafficShaping,
    showTrafficShapingDialog,
    showInsufficientDataDialog,
    closeTrafficShapingDialog,
    openTrafficShapingDialog,
    closeInsufficientDataDialog,
    showAITrafficControlDetailsDialog,
    setShowAITrafficControlDetailsDialog,
    openAITrafficControlDetailsDialog,
    closeAITrafficControlDetailsDialog,
    selectedTrafficControlId,
    onRecurringToggle,
  };
}
